"use client"

import * as React from "react"
import * as Icons from "lucide-react"

export type IconName = keyof typeof Icons

export interface IconProps extends React.SVGAttributes<SVGSVGElement> {
  /** Named Lucide icon to render. Defaults to `X`. */
  name?: IconName
  /** Pixel size of the icon box. Defaults to 24. */
  size?: number
  /** Stroke width in pixels. Defaults to the `--icon-stroke-width` CSS var (or 2). */
  strokeWidth?: number
  /** When true, stroke width doesn’t scale with size (Lucide prop). */
  absoluteStrokeWidth?: boolean
}

// Read the default stroke width from the root `--icon-stroke-width` CSS var
// exactly once per session and cache it. The previous implementation ran
// `getComputedStyle(document.documentElement)` inside the default-prop
// evaluation of every <Icon> render, which triggers a forced reflow each
// time — a measurable chunk of "forced reflow" time in Lighthouse traces.
let cachedStrokeWidth: number | undefined
function getDefaultStrokeWidth(): number {
  if (cachedStrokeWidth !== undefined) return cachedStrokeWidth
  if (typeof window === "undefined") return 2
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue("--icon-stroke-width")
    .trim()
  cachedStrokeWidth = Number(raw) || 2
  return cachedStrokeWidth
}

export function Icon({
  name = "X",
  size = 24,
  strokeWidth,
  absoluteStrokeWidth = true,
  ...svgProps
}: IconProps) {
  const resolvedStroke = strokeWidth ?? getDefaultStrokeWidth()
  const LucideIcon = (Icons[name] ?? Icons.X) as React.ComponentType<
    React.SVGProps<SVGSVGElement> & { size?: number; absoluteStrokeWidth?: boolean }
  >
  return (
    <LucideIcon
      size={size}
      strokeWidth={resolvedStroke}
      absoluteStrokeWidth={absoluteStrokeWidth}
      {...svgProps}
    />
  )
}

export default Icon


